import type { IWrappedProperty } from '@witkac/jschema';
import { computed, type ComputedRef, getCurrentInstance, onMounted, ref, type Ref } from 'vue';

import { confirmService } from '../services/confirm.service';
import type { Tab } from '../typings/tab';

function traverseTree(cmp: any, result: any[]) {
  if (cmp.component) {
    cmp = cmp.component;
  }

  if (cmp.subTree?.component) {
    cmp = cmp.subTree.component;
  }

  const children = cmp.subTree ? cmp.subTree.children : cmp.children;
  if (!children) {
    return result;
  }

  if (typeof children === 'string') {
    return result;
  }

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const model: IWrappedProperty = child.props?.model;

    if (model) {
      const validationResult = model.$validate();
      if (validationResult) {
        result.push(validationResult);
      }
    }

    if (model) {
      traverseTree(child, result);
    } else {
      traverseTree(child, result);
    }
  }
  return result;
}

export function useTabs<T extends IWrappedProperty>(
  schema: T,
  tabs: Tab[],
): {
  isFirstIndex: ComputedRef<boolean>;
  isLastIndex: ComputedRef<boolean>;
  tabs: Ref<Tab[]>;
  selectedTab: Ref<Tab>;
  nextTab: () => any;
  prevTab: () => any;
  setTab: (tab: Tab) => any;
} {
  let $: any;
  const instance = getCurrentInstance();
  const tabsRef = ref(isTabDisabled());
  const selectedTab = ref(tabsRef.value[0]);

  onMounted(() => {
    $ = getCurrentInstance();
  });

  function isTabDisabled() {
    if (!schema.$external.tabsEnabled.length) {
      return tabs;
    }
    return tabs.map((tab, index) => {
      tab.disabled = !schema.$external.tabsEnabled.includes(index);
      return { ...tab };
    });
  }

  function nextTab() {
    const index = tabsRef.value.findIndex((tab) => tab === selectedTab.value);
    if (index < 0 || index + 1 >= tabsRef.value.length) {
      return;
    }

    setTab(tabsRef.value[index + 1]);
  }

  function prevTab() {
    const index = tabsRef.value.findIndex((tab) => tab === selectedTab.value);

    if (index < 0 || index - 1 < 0) {
      return;
    }

    setTab(tabsRef.value[index - 1]);
  }

  function validateCurrentTab(): IWrappedProperty[] {
    const result: any[] = [];

    traverseTree($, result);
    return result;
  }

  function setTab(tab: Tab) {
    if (tab === selectedTab.value) {
      return;
    }
    const errors = validateCurrentTab();
    instance?.emit('validate', errors);

    if (errors.length === 0) {
      selectedTab.value = tab;
      instance?.emit('tab-changed', tab);
      return;
    }

    return confirmService
      .confirm(
        'Nieprawidłowo wypełniono pola w tej części wniosku. Czy chcesz przejść dalej, a błędy poprawić później?',
      )
      .then(() => (selectedTab.value = tab))
      .then(() => instance?.emit('tab-changed', tab))
      .then(() => window.scrollTo(0, 0));
  }

  return {
    isFirstIndex: computed(() => tabsRef.value[0] === selectedTab.value),
    isLastIndex: computed(() => tabsRef.value[tabsRef.value.length - 1] === selectedTab.value),
    tabs: tabsRef,
    selectedTab,

    nextTab,
    prevTab,
    setTab,
  };
}
